import React, { useState, useRef } from 'react';
import { apiFetch } from '../api';

const StudentOnboardingModal = ({ onComplete, onSkip }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    profile_image: null,
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_000_000) {
      setError('Image must be under 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setForm((p) => ({ ...p, profile_image: dataUrl }));
      setPreview(dataUrl);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch('/api/students/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          date_of_birth: form.date_of_birth || undefined,
          profile_image: form.profile_image || undefined,
          onboarding_complete: true,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to save profile.');
        return;
      }
      const data = await res.json();
      onComplete(data.profile);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    // Don't mark complete — modal will reappear next login
    onSkip();
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <h2 style={styles.heading}>Welcome! Set up your profile</h2>
        <p style={styles.sub}>This helps your tutor personalise your experience.</p>

        {/* Avatar picker */}
        <div style={styles.avatarRow}>
          <div
            style={styles.avatarCircle}
            onClick={() => fileRef.current?.click()}
            title="Upload photo"
          >
            {preview
              ? <img src={preview} alt="Profile" style={styles.avatarImg} />
              : <span style={styles.avatarPlaceholder}>📷</span>}
          </div>
          <button type="button" style={styles.photoBtn} onClick={() => fileRef.current?.click()}>
            {preview ? 'Change photo' : 'Upload photo'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageChange}
          />
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>First name <span style={styles.req}>*</span></label>
            <input
              style={styles.input}
              value={form.first_name}
              onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
              placeholder="Jane"
              autoFocus
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Last name <span style={styles.req}>*</span></label>
            <input
              style={styles.input}
              value={form.last_name}
              onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
              placeholder="Smith"
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Date of birth <span style={styles.opt}>(optional)</span></label>
          <input
            type="date"
            style={styles.input}
            value={form.date_of_birth}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setForm((p) => ({ ...p, date_of_birth: e.target.value }))}
          />
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.actions}>
          <button type="button" style={styles.skipBtn} onClick={handleSkip}>
            Skip for now
          </button>
          <button
            type="button"
            style={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9000,
  },
  modal: {
    background: 'var(--lrbrain-surface, #fff)',
    borderRadius: '12px',
    padding: '2rem',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  heading: { margin: 0, fontSize: '1.3rem', color: 'var(--lrbrain-text, #0f172a)' },
  sub: { margin: 0, fontSize: '0.875rem', color: '#64748b' },
  avatarRow: { display: 'flex', alignItems: 'center', gap: '1rem' },
  avatarCircle: {
    width: 72, height: 72, borderRadius: '50%',
    background: '#e2e8f0', display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', overflow: 'hidden',
    border: '2px dashed #94a3b8', flexShrink: 0,
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarPlaceholder: { fontSize: '1.6rem' },
  photoBtn: {
    background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px',
    padding: '0.4rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem',
    color: 'var(--lrbrain-text, #0f172a)',
  },
  row: { display: 'flex', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 },
  label: { fontSize: '0.8rem', fontWeight: 600, color: '#475569' },
  req: { color: '#ef4444' },
  opt: { fontWeight: 400, color: '#94a3b8' },
  input: {
    padding: '0.5rem 0.75rem', borderRadius: '6px',
    border: '1px solid #cbd5e1', fontSize: '0.9rem',
    background: 'var(--lrbrain-input-bg, #f8fafc)',
    color: 'var(--lrbrain-text, #0f172a)',
    width: '100%', boxSizing: 'border-box',
  },
  error: { margin: 0, color: '#dc2626', fontSize: '0.85rem' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' },
  skipBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#64748b', fontSize: '0.875rem', padding: '0.5rem',
  },
  saveBtn: {
    background: 'var(--lrbrain-primary, #2563eb)', color: '#fff',
    border: 'none', borderRadius: '7px', padding: '0.55rem 1.4rem',
    fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
  },
};

export default StudentOnboardingModal;
